import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/useApi.ts';
import { money, shortDate } from '../lib/format.ts';
import {
  LEAD_STATUS_TONE,
  STAGE_LABEL,
  STAGE_TONE,
  labelFor,
  type LeadListRow,
} from '../lib/types.ts';
import {
  Badge,
  Card,
  EmptyState,
  SkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '../components/ui/index.ts';
import { SearchIcon } from '../components/icons.tsx';

interface Response {
  leads: LeadListRow[];
  total: number;
}

/**
 * The way into a lead. Not in the original section 3 list, but the lead
 * detail page needs something to link from other than a typed URL, and the
 * board only shows leads currently in an open stage.
 */
export default function Leads() {
  const [search, setSearch] = useState('');
  const path = `/leads?limit=100${search.trim() === '' ? '' : `&q=${encodeURIComponent(search.trim())}`}`;
  const { data, error, loading } = useApi<Response>(path);

  return (
    <div className="space-y-5">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tighter">Leads</h1>
          <p className="mt-0.5 text-xs text-fg-subtle">
            {data ? `${data.total} people` : 'Everyone who has ever entered the funnel'}
          </p>
        </div>
        <div className="relative">
          <SearchIcon
            size={14}
            className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-fg-subtle"
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name or practice"
            className="h-8 w-64 rounded-md border border-border bg-surface pr-3 pl-8 text-sm text-fg placeholder:text-fg-faint focus:border-border-strong"
          />
        </div>
      </header>

      {error && <p className="text-sm text-lost">{error}</p>}

      <Card flush>
        <Table>
          <THead>
            <TH>Lead</TH>
            <TH>Stage</TH>
            <TH>Status</TH>
            <TH>Source</TH>
            <TH numeric>Value</TH>
            <TH numeric>Last activity</TH>
          </THead>
          <TBody>
            {loading && <SkeletonRows rows={8} columns={6} />}
            {!loading &&
              data?.leads.map((lead) => (
                <TR key={lead.id}>
                  <TD>
                    <Link
                      to={`/leads/${lead.id}`}
                      className="font-medium text-fg transition hover:text-accent-text"
                    >
                      {lead.full_name}
                    </Link>
                    <span className="ml-2 text-fg-subtle">
                      {lead.practice_name ?? 'Practice unknown'}
                    </span>
                  </TD>
                  <TD>
                    {lead.current_stage ? (
                      <Badge tone={STAGE_TONE[lead.current_stage] ?? 'neutral'} dot>
                        {labelFor(STAGE_LABEL, lead.current_stage)}
                      </Badge>
                    ) : (
                      <span className="text-fg-faint">—</span>
                    )}
                  </TD>
                  <TD>
                    <Badge tone={LEAD_STATUS_TONE[lead.status] ?? 'neutral'}>
                      {lead.status.replace(/_/g, ' ')}
                    </Badge>
                  </TD>
                  <TD muted>{lead.source}</TD>
                  <TD numeric>
                    {lead.deal_value && Number(lead.deal_value) > 0 ? money(lead.deal_value) : '—'}
                  </TD>
                  <TD numeric muted>
                    {shortDate(lead.current_stage_at)}
                  </TD>
                </TR>
              ))}
          </TBody>
        </Table>

        {!loading && data && data.leads.length === 0 && (
          <EmptyState
            icon={<SearchIcon size={18} />}
            title="No leads match that search"
            description={
              search.trim() === ''
                ? 'Import the call tracker with npm run seed, or log a call to create the first lead.'
                : 'Try a shorter search, or clear it to see everyone.'
            }
          />
        )}
      </Card>
    </div>
  );
}
